<!-- \src\components\SeasonsEditComponent.vue -->
<template>
  <div
    class="modal fade"
    :id="`editSeasonModal-${season.id}`"
    ref="modalRef"
    tabindex="-1"
    aria-labelledby="editSeasonModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editSeasonModalLabel">Edit Season</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="editSeason(season)">
            <div class="mb-3">
              <label for="seasonDescription" class="form-label">Name</label>
              <input
                type="text"
                class="form-control"
                id="seasonDescription"
                v-model="description"
                required
                placeholder="Enter season name"
              />
            </div>
            <div class="mb-3">
              <label for="seasonStartDate" class="form-label">Start Date</label>
              <input
                type="date"
                class="form-control"
                id="seasonStartDate"
                v-model="startDate"
                required
              />
            </div>
            <div class="mb-3">
              <label for="seasonEndDate" class="form-label">End Date</label>
              <input
                type="date"
                class="form-control"
                id="seasonEndDate"
                v-model="endDate"
                required
              />
            </div>
            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="overrideSeason"
                v-model="overrideSeason"
              />
              <label class="form-check-label" for="overrideSeason">
                Override Season
              </label>
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  PropType,
  defineComponent,
  ref,
  watch,
  computed,
  onMounted,
  onUnmounted,
} from 'vue';
import { Modal } from 'bootstrap';
import { Season, SeasonBatchSaveRequest } from '../api';
import { seasonBatchApi } from '@/apiClient';
import eventBus, { EventType } from '../eventBus';
import { validateSeasonName } from '@/utils/seasonUtils';
import { formatDateToInput } from '@/utils/dateUtils';

export default defineComponent({
  name: 'SeasonsEditComponent',
  props: {
    season: {
      type: Object as PropType<Season>,
      required: true,
    },
    existingSeasons: {
      type: Array as PropType<Season[]>,
      default: () => [],
    },
  },
  emits: ['season-updated'],
  setup(props, { emit }) {
    const description = ref(props.season.description || '');
    const startDate = ref(formatDateToInput(props.season.startDate ?? ''));
    const endDate = ref(formatDateToInput(props.season.endDate ?? ''));
    const overrideSeason = ref(false);
    const errorMessage = ref('');
    const modalRef = ref<HTMLElement | null>(null);

    const today = computed(() => {
      const date = new Date();
      return formatDateToInput(date.toISOString());
    });

    const resetForm = () => {
      description.value = props.season.description || '';
      startDate.value = formatDateToInput(props.season.startDate ?? '');
      endDate.value = formatDateToInput(props.season.endDate ?? '');
      overrideSeason.value = false;
      errorMessage.value = '';
    };

    watch(
      () => props.season,
      (newSeason) => {
        description.value = newSeason.description || '';
        startDate.value = formatDateToInput(newSeason.startDate ?? '');
        endDate.value = formatDateToInput(newSeason.endDate ?? '');
      }
    );

    const checkDateOverlap = (start: string, end: string) => {
      if (!props.existingSeasons) {
        console.warn('existingSeasons is undefined');
        return false;
      }
      const newStart = new Date(start).toISOString();
      const newEnd = new Date(end).toISOString();

      return props.existingSeasons.some((existingSeason) => {
        // Skip the current season
        if (existingSeason.id === props.season.id) return false;

        const seasonStart = existingSeason.startDate
          ? new Date(existingSeason.startDate.split('T')[0]).toISOString()
          : null;
        const seasonEnd = existingSeason.endDate
          ? new Date(existingSeason.endDate.split('T')[0]).toISOString()
          : null;

        if (!seasonStart || !seasonEnd) {
          return false; // Skip seasons with undefined dates
        }

        return (
          (newStart >= seasonStart && newStart <= seasonEnd) ||
          (newEnd >= seasonStart && newEnd <= seasonEnd) ||
          (newStart <= seasonStart && newEnd >= seasonEnd)
        );
      });
    };

    const validateDates = () => {
      const originalStartDate = formatDateToInput(
        new Date(props.season.startDate!).toISOString()
      );
      const originalEndDate = formatDateToInput(
        new Date(props.season.endDate!).toISOString()
      );

      if (
        originalStartDate !== startDate.value &&
        startDate.value < today.value
      ) {
        errorMessage.value = 'Start date cannot be in the past.';
        return false;
      }

      if (
        originalEndDate !== endDate.value &&
        endDate.value < startDate.value
      ) {
        errorMessage.value = 'End date cannot be before the start date.';
        return false;
      }

      if (
        !overrideSeason.value &&
        checkDateOverlap(startDate.value, endDate.value)
      ) {
        errorMessage.value =
          'The selected dates overlap with an existing season.';
        return false;
      }
      errorMessage.value = '';
      return true;
    };

    async function editSeason(season: Season) {
      if (
        !validateDates() ||
        (props.season.description !== description.value &&
          !validateSeasonName(
            description.value,
            props.existingSeasons,
            errorMessage
          ))
      ) {
        return;
      }

      const updatedSeason: Partial<Season> = {
        ...season,
        description: description.value,
        startDate: startDate.value,
        endDate: endDate.value,
        priority: overrideSeason.value ? 1 : 0,
        seasonChannels: [],
      };

      const seasonBatchSaveRequest: SeasonBatchSaveRequest = {
        editedSeasons: [updatedSeason],
        deletedSeasonIds: [],
      };

      try {
        const response = await seasonBatchApi.seasonBatchSavePut(
          props.season.courseId,
          seasonBatchSaveRequest
        );

        if (response.status === 200) {
          console.log('Season Edited:', updatedSeason);

          eventBus.emit(EventType.RefreshSeasons);
          emit('season-updated', updatedSeason);

          const element = modalRef.value;
          if (element) {
            const bootstrapModal = Modal.getOrCreateInstance(element);
            bootstrapModal.hide();
          }
        }
      } catch (error) {
        console.error('Error updating season:', error);
        errorMessage.value = 'An error occurred while updating the season.';
      }
    }

    onMounted(() => {
      if (!modalRef.value) {
        return;
      }
      modalRef.value.addEventListener('show.bs.modal', resetForm);
      modalRef.value.addEventListener('hidden.bs.modal', resetForm);
    });

    onUnmounted(() => {
      const element = modalRef.value;
      if (!element) {
        return;
      }
      element.removeEventListener('show.bs.modal', resetForm);
      element.removeEventListener('hidden.bs.modal', resetForm);
    });

    return {
      description,
      startDate,
      endDate,
      overrideSeason,
      editSeason,
      errorMessage,
      today,
      checkDateOverlap,
      modalRef,
    };
  },
});
</script>

<style scoped>
.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.modal-content {
  border-radius: 8px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
}

.form-check-label {
  margin-left: 5px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}
</style>
